import { record } from '@rrweb/record';
import type { eventWithTime } from '@rrweb/types';
import {
  appendToChunk,
  closeChunk,
  createOpenChunk,
  enforceRetention,
  findOpenChunk,
  getOrCreateTabId,
} from '@/integrations/idb/models/rrweb';
import { RRWEB_ENABLED } from '@/lib/env';
import { startUrlTracking, stopUrlTracking } from '../hooks/use-url-recorder';
import { RRWEB_URL_EVENT_TAG } from '../types';
import {
  drainNetworkCalls,
  peekNetworkCalls,
  restoreNetworkCalls,
} from './api-recorder';

const APPEND_INTERVAL_MS = 3000;
const CHUNK_DURATION_MS = 5 * 60 * 1000;

const startRrweb = (onEvent: (event: eventWithTime) => void) =>
  record({
    emit: onEvent,
    maskAllInputs: false,
  }) ?? undefined;

const createRecordingManager = () => {
  let stopFn: (() => void) | undefined;
  let isPaused = false;
  const buffer: eventWithTime[] = [];
  let tabId = '';
  let openChunkId: string | undefined;
  let chunkStartTime = 0;
  let elapsedMs = 0;
  let recordingStartedAt = 0;
  let appendTimer: number | null = null;
  let appendQueue = Promise.resolve();
  let initializationPromise: Promise<void> = Promise.resolve();

  function ensureAppendTimer(): void {
    if (appendTimer !== null) return;
    appendTimer = window.setInterval(() => {
      if (!isPaused) void appendTail();
    }, APPEND_INTERVAL_MS);
  }

  function recordUrlChange(href: string): void {
    if (!stopFn) return;
    try {
      record.addCustomEvent(RRWEB_URL_EVENT_TAG, { href });
    } catch (error) {
      console.error('[rrweb] record url failed:', error);
    }
  }

  const startRecording = (): void => {
    if (!RRWEB_ENABLED || stopFn || appendTimer || isPaused) return;

    const now = Date.now();
    tabId = getOrCreateTabId();
    elapsedMs = 0;
    openChunkId = undefined;
    chunkStartTime = now;
    recordingStartedAt = now;

    stopFn = startRrweb((event) => {
      buffer.push(event);
    });
    startUrlTracking(recordUrlChange);

    initializationPromise = findOpenChunk(tabId)
      .then((chunk) => {
        if (!chunk) return;
        openChunkId = chunk.id;
        elapsedMs = chunk.elapsedMs;
        chunkStartTime = chunk.startTime;
      })
      .catch((error: unknown) => {
        console.error('[rrweb] restore open chunk failed:', error);
      })
      .finally(() => {
        if (!isPaused) ensureAppendTimer();
      });
  };

  async function rotateChunk(): Promise<void> {
    if (!openChunkId) return;
    await closeChunk(openChunkId, elapsedMs);
    openChunkId = undefined;
    elapsedMs = 0;
    chunkStartTime = Date.now();
    recordingStartedAt = chunkStartTime;
    stopFn?.();
    stopFn = startRrweb((event) => {
      buffer.push(event);
    });
  }

  async function flushAndPersist(): Promise<void> {
    if (buffer.length === 0 && peekNetworkCalls().length === 0) return;

    if (!openChunkId) {
      openChunkId = await createOpenChunk({
        tabId,
        startTime: chunkStartTime,
      });
    }

    const chunkId = openChunkId;
    const events = buffer.splice(0);
    const networkCalls = drainNetworkCalls();

    try {
      await appendToChunk(chunkId, events, networkCalls, { elapsedMs });
    } catch (error) {
      buffer.unshift(...events);
      restoreNetworkCalls(networkCalls);
      // Another tab may have cleared the database, so drop the chunk reference and let the next flush recreate it.
      openChunkId = undefined;
      throw error;
    }

    void enforceRetention(tabId).catch((error: unknown) => {
      console.error('[rrweb] retention failed:', error);
    });

    if (elapsedMs >= CHUNK_DURATION_MS) {
      await rotateChunk();
    }
  }

  async function appendTail(): Promise<void> {
    const previousAppend = appendQueue;
    const nextAppend = (async () => {
      await previousAppend.catch(() => {});
      await initializationPromise;

      try {
        const now = Date.now();
        if (recordingStartedAt) {
          elapsedMs += now - recordingStartedAt;
          recordingStartedAt = isPaused ? 0 : now;
        }

        await flushAndPersist();
      } catch (error) {
        console.error('[rrweb] appendTail failed:', error);
      }
    })();
    appendQueue = nextAppend.catch(() => {});
    return nextAppend;
  }

  const pauseRecording = (): void => {
    if (isPaused) return;
    if (recordingStartedAt) {
      elapsedMs += Date.now() - recordingStartedAt;
      recordingStartedAt = 0;
    }
    stopUrlTracking();
    stopFn?.();
    stopFn = undefined;
    isPaused = true;
  };

  const resumeRecording = (): void => {
    if (!(RRWEB_ENABLED && isPaused)) return;
    isPaused = false;
    recordingStartedAt = Date.now();
    stopFn = startRrweb((event) => {
      buffer.push(event);
    });
    startUrlTracking(recordUrlChange);
    ensureAppendTimer();
  };

  let suspendRequested = false;

  const suspendRecording = async (): Promise<void> => {
    suspendRequested = true;
    try {
      await appendTail();
    } finally {
      if (suspendRequested) pauseRecording();
    }
  };

  const releaseRecording = (): void => {
    if (!suspendRequested) return;
    suspendRequested = false;
    resumeRecording();
  };

  return {
    startRecording,
    suspendRecording,
    releaseRecording,
  };
};

export const { startRecording, suspendRecording, releaseRecording } =
  createRecordingManager();
